/**
 * API 클라이언트
 *
 * 모든 API 호출은 이 파일을 통해 이루어집니다.
 * - 공통 envelope 패턴 자동 처리
 * - 네트워크 오류를 ApiError로 정규화
 * - credentials: 'include'로 세션 쿠키 자동 전송
 *
 * 자세한 사용법은 docs/05-api-spec.md 참조
 */

import type { ApiResponse, ApiError } from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeout?: number;
}

const DEFAULT_TIMEOUT = 10000; // 10초

/**
 * 네트워크 오류를 ApiError 형식으로 정규화
 */
function networkError(message: string): ApiError {
  return {
    success: false,
    code: 'NETWORK_ERROR',
    message,
    data: null,
    meta: {
      timestamp: new Date().toISOString(),
      request_id: '',
    },
  };
}

/**
 * 응답 객체가 표준 envelope 형식인지 검증
 */
function isValidEnvelope(json: unknown): json is ApiResponse<unknown> {
  if (typeof json !== 'object' || json === null) return false;
  const obj = json as Record<string, unknown>;
  return 'success' in obj && 'code' in obj && typeof obj.success === 'boolean';
}

export async function apiCall<T>(
  path: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { body, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  // 타임아웃 처리
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    const res = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...fetchOptions.headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    clearTimeout(timeoutId);

    // body 파싱
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      return networkError('서버 응답을 처리할 수 없습니다.');
    }

    // envelope 검증
    if (!isValidEnvelope(json)) {
      console.error('[API] Invalid response format:', json);
      return networkError('잘못된 형식의 응답을 받았습니다.');
    }

    return json as ApiResponse<T>;
  } catch (err) {
    clearTimeout(timeoutId);

    if (err instanceof DOMException && err.name === 'AbortError') {
      return networkError('요청 시간이 초과되었습니다.');
    }

    return networkError('네트워크 오류가 발생했습니다.');
  }
}

/**
 * GET helper
 */
export function apiGet<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
  return apiCall<T>(path, { ...options, method: 'GET' });
}

/**
 * POST helper
 */
export function apiPost<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return apiCall<T>(path, { ...options, method: 'POST', body });
}
